/**
 * Keeps this browser's Velora windows in step (bus.ts):
 *
 * - Selected patient. Every window announces its selection. A pop-out that
 *   follows the selected patient (no `?pin=1`) takes any other window's
 *   selection and, on a per-patient page, opens the same page for them. The
 *   main window takes a pop-out's selection (a schedule on the second
 *   monitor drives it) and switches its family file, chart, account or
 *   imaging page; pages where people type keep their patient
 *   (popout.ts `mainFollows`). Main tabs do not drive each other, as before.
 *   A locked pop-out neither follows nor drives.
 * - Pop-out registry. Pop-outs say hello (and bye); main windows count them
 *   for the Pop out menu and can close them all.
 * - Pop-out geometry and workspace. A pop-out remembers its size and
 *   position per module on this device, and marks itself open or closed in
 *   the workspace (Restore my workspace).
 *
 * Sign-out, lock and idle time are shared by the identity session itself
 * (modules/identity/session.tsx), so they work in every window.
 */
import { useLocation, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import {
  getCurrentPatient,
  setCurrentPatient,
  subscribeCurrentPatient,
  type CurrentPatient,
} from '../patient/current.js';
import { splitHref } from '../patient/follow.js';
import { onWindowMessage, postWindowMessage, WINDOW_ID, type WindowMessage } from './bus.js';
import {
  mainFollows,
  patientOnPage,
  popoutSpec,
  retargetPatient,
  specHref,
  type PopoutState,
} from './popout.js';
import { closeInWorkspace, saveGeometry, touchWorkspace, updateWorkspace } from './workspace.js';

/* ---------- open pop-outs, as seen from a main window ---------- */

export interface OpenPopout {
  id: string;
  key: string;
  label: string;
  pinned: boolean;
}

let open: readonly OpenPopout[] = [];
const openListeners = new Set<() => void>();
function setOpen(next: readonly OpenPopout[]) {
  open = next;
  for (const l of openListeners) l();
}
const subscribeOpen = (l: () => void) => {
  openListeners.add(l);
  return () => openListeners.delete(l);
};
const getOpen = () => open;

/** The pop-out windows open in this browser (main windows only; empty in a pop-out). */
export function useOpenPopouts(): readonly OpenPopout[] {
  return useSyncExternalStore(subscribeOpen, getOpen, getOpen);
}

export function closeAllPopouts(): void {
  postWindowMessage({ t: 'close-all', from: WINDOW_ID });
}

/* ---------- patient selection ---------- */

/** True while this window applies another window's selection (not echoed back). */
let applyingRemote = false;

function applyRemote(patient: CurrentPatient | null) {
  applyingRemote = true;
  try {
    setCurrentPatient(patient);
  } finally {
    applyingRemote = false;
  }
}

export interface WindowSyncProps {
  /** This window's pop-out state; null in a main window. */
  popout: PopoutState | null;
  /** The pop-out's title (module or page name), for the main window's menu. */
  label?: string;
}

export function WindowSync({ popout, label = '' }: WindowSyncProps) {
  const router = useRouter();
  const location = useLocation();
  const role = popout ? 'popout' : 'main';
  const pinned = popout?.pinned ?? false;
  const key = popout?.key ?? null;

  // Announce this window's own selection changes.
  useEffect(() => {
    if (pinned) return undefined;
    let last = getCurrentPatient();
    return subscribeCurrentPatient(() => {
      const now = getCurrentPatient();
      if (now === last) return;
      last = now;
      if (applyingRemote) return;
      postWindowMessage({ t: 'patient', from: WINDOW_ID, role, patient: now });
    });
  }, [role, pinned]);

  // Take other windows' selections.
  useEffect(() => {
    if (pinned) return undefined;
    const navigateTo = (pathname: string, search: Record<string, unknown>, replace: boolean) =>
      void router.navigate({ to: pathname, search, replace } as never);
    return onWindowMessage((m: WindowMessage) => {
      if (m.t !== 'patient') return;
      // Main tabs never drive each other (one selected patient per tab, as before).
      if (role === 'main' && m.role === 'main') return;
      const loc = router.state.location;
      const search = loc.search as Record<string, unknown>;
      const p = m.patient;
      const onPatientPage = patientOnPage(loc.pathname, search) !== null;
      if (!p) {
        // Unselect elsewhere: drop the selection unless this window shows one patient's page.
        if (!onPatientPage) applyRemote(null);
        return;
      }
      applyRemote(p);
      const target = retargetPatient(loc.pathname, search, p.id);
      if (role === 'popout') {
        if (target) {
          if (target.pathname !== loc.pathname || target.search['patient'] !== search['patient'])
            navigateTo(target.pathname, target.search, true);
          return;
        }
        // A follow pop-out on its module's own page (no patient yet): open theirs.
        const spec = key ? popoutSpec(key) : undefined;
        if (spec?.patientPath && loc.pathname === spec.path) {
          const href = splitHref(specHref(spec, p.id));
          navigateTo(href.to, { ...search, ...(href.search ?? {}) }, true);
        }
        return;
      }
      if (
        target &&
        mainFollows(loc.pathname) &&
        target.pathname + JSON.stringify(target.search) !== loc.pathname + JSON.stringify(search)
      ) {
        navigateTo(target.pathname, target.search, false);
      }
    });
  }, [router, role, pinned, key]);

  // Main window: keep the list of open pop-outs.
  useEffect(() => {
    if (popout) return undefined;
    const off = onWindowMessage((m) => {
      if (m.t === 'hello') {
        const entry: OpenPopout = { id: m.from, key: m.key, label: m.label, pinned: m.pinned };
        const rest = open.filter((o) => o.id !== m.from);
        setOpen([...rest, entry]);
      } else if (m.t === 'bye') {
        if (open.some((o) => o.id === m.from)) setOpen(open.filter((o) => o.id !== m.from));
      }
    });
    postWindowMessage({ t: 'who', from: WINDOW_ID });
    return off;
  }, [popout]);

  // Pop-out: hello / bye, close-all, geometry and workspace.
  const labelRef = useRef(label);
  labelRef.current = label;
  const pageHasPatient =
    patientOnPage(location.pathname, location.search as Record<string, unknown>) !== null;
  const plainPath = pageHasPatient ? null : location.pathname;
  useEffect(() => {
    if (!key) return undefined;
    const hello = () =>
      postWindowMessage({ t: 'hello', from: WINDOW_ID, key, label: labelRef.current, pinned });
    hello();
    const spec = popoutSpec(key);
    const path = plainPath && plainPath !== spec?.path ? plainPath : null;
    const touch = () =>
      updateWorkspace((ws) => touchWorkspace(ws, { key, path, pinned }, Date.now()));
    touch();
    const beat = window.setInterval(touch, 20_000);
    const off = onWindowMessage((m) => {
      if (m.t === 'who') hello();
      else if (m.t === 'close-all') window.close();
    });
    return () => {
      window.clearInterval(beat);
      off();
    };
  }, [key, pinned, plainPath, label]);

  useEffect(() => {
    if (!key) return undefined;
    const measure = () => ({
      left: window.screenX,
      top: window.screenY,
      width: window.innerWidth,
      height: window.innerHeight,
    });
    let last = JSON.stringify(measure());
    const save = () => {
      const g = measure();
      const s = JSON.stringify(g);
      if (s === last) return;
      last = s;
      saveGeometry(key, g);
    };
    // There is no "window moved" event: look every second and a half.
    const poll = window.setInterval(save, 1500);
    let t: number | undefined;
    const onResize = () => {
      window.clearTimeout(t);
      t = window.setTimeout(save, 300);
    };
    const onHide = () => {
      save();
      postWindowMessage({ t: 'bye', from: WINDOW_ID });
      updateWorkspace((ws) => closeInWorkspace(ws, key, Date.now()));
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('pagehide', onHide);
    return () => {
      window.clearInterval(poll);
      window.clearTimeout(t);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pagehide', onHide);
    };
  }, [key]);

  return null;
}
